import { useMemo } from "react";
import { useSelector } from "react-redux";

/**
 * Normalize action name to standard format (create, read, update, delete)
 * @param {string} action - Action name (can be 'c', 'r', 'u', 'd' or full names like "CREATE", "create", etc.)
 * @returns {string} - Normalized action name in lowercase
 */
const normalizeAction = (action) => {
  const actionMap = {
    c: "create",
    r: "read",
    u: "update",
    d: "delete",
    create: "create",
    read: "read",
    update: "update",
    delete: "delete",
    CREATE: "create",
    READ: "read",
    UPDATE: "update",
    DELETE: "delete",
  };
  return actionMap[action] || action?.toLowerCase();
};

/**
 * Normalize resource type to handle different casing formats
 * @param {string} resourceType - Resource type (e.g., "LEADS", "CUSTOMER_PAYMENTS", "customer_payments")
 * @returns {string} - Normalized resource type
 */
const normalizeResourceType = (resourceType) => {
  if (!resourceType) return "";
  // Convert to uppercase for consistency
  return resourceType.toUpperCase();
};

/**
 * Get permissions from role object
 * Handles the actual API structure where permissions is an array of objects:
 * [{ id: 1, name: "CREATE", resourceType: "LEADS" }, ...]
 * 
 * @param {object} roleObject - Role object with permissions
 * @returns {object} - Permissions object (resourceType -> {create, read, update, delete})
 */
const getPermissionsFromRole = (roleObject) => {
  if (!roleObject || typeof roleObject !== "object") {
    return {};
  }

  // Handle array-based permissions structure (actual API format)
  if (roleObject.permissions && Array.isArray(roleObject.permissions)) {
    const permissionsObj = {};
    
    roleObject.permissions.forEach((perm) => {
      if (typeof perm === "object" && perm.resourceType && perm.name) {
        const resourceType = normalizeResourceType(perm.resourceType);
        const action = normalizeAction(perm.name);
        
        // Initialize resourceType if it doesn't exist
        if (!permissionsObj[resourceType]) {
          permissionsObj[resourceType] = {};
        }
        
        // Set the action to true
        permissionsObj[resourceType][action] = true;
      }
    });
    
    return permissionsObj;
  }
  
  // Handle legacy object-based structure (if needed)
  if (roleObject.permissions && typeof roleObject.permissions === "object" && !Array.isArray(roleObject.permissions)) {
    // Structure: { permissions: { resource: { create: true, read: true, ... } } }
    return roleObject.permissions;
  }
  
  // Handle alternative permission property
  if (roleObject.permission && typeof roleObject.permission === "object") {
    if (Array.isArray(roleObject.permission)) {
      // Same array structure as permissions
      const permissionsObj = {};
      roleObject.permission.forEach((perm) => {
        if (typeof perm === "object" && perm.resourceType && perm.name) {
          const resourceType = normalizeResourceType(perm.resourceType);
          const action = normalizeAction(perm.name);
          if (!permissionsObj[resourceType]) {
            permissionsObj[resourceType] = {};
          }
          permissionsObj[resourceType][action] = true;
        }
      });
      return permissionsObj;
    }
    return roleObject.permission;
  }

  return {};
};

/**
 * Hook to check if the current user has a specific CRUD permission
 * 
 * @param {string} resource - Resource name (e.g., "customer_payments", "contractor_payouts")
 * @param {string} action - Action name (e.g., "create", "read", "update", "delete" or "c", "r", "u", "d")
 * @returns {object} - { hasPermission: boolean, isLoading: boolean, userRole: string }
 */
export const useCRUDPermission = (resource, action) => {
  const { userData, userRoleWithPermissions, contractorRoles } = useSelector((state) => state.auth);

  return useMemo(() => {
    // If resource or action is null/undefined, return early
    if (!resource || !action) {
      return {
        hasPermission: false,
        isLoading: false,
        userRole: userData?.role || null,
      };
    }

    const userRole = userData?.role || null;

    // If no user role, return false
    if (!userRole) {
      return {
        hasPermission: false,
        isLoading: !userData,
        userRole: null,
      };
    }

    // Contractor, Designer, and Supplier roles have full access - bypass permission checks
    // These are the main "superadmin" roles for their respective business types
    const normalizedUserRole = userRole?.toLowerCase();
    if (normalizedUserRole === "contractor" || normalizedUserRole === "designer" || normalizedUserRole === "supplier") {
      return {
        hasPermission: true,
        isLoading: false,
        userRole,
      };
    }

    // If user role permissions haven't loaded yet, return loading state
    if (!userRoleWithPermissions) {
      return {
        hasPermission: false,
        isLoading: true,
        userRole,
      };
    }

    // Get permissions from user's role
    const permissions = getPermissionsFromRole(userRoleWithPermissions);

    // Normalize action
    const normalizedAction = normalizeAction(action);

    // Normalize resource type for comparison
    const normalizedResource = normalizeResourceType(resource);

    // Check if resource exists in permissions
    if (!permissions[normalizedResource]) {
      return {
        hasPermission: false,
        isLoading: false,
        userRole,
      };
    }

    // Check if action is allowed for this resource
    const resourcePermissions = permissions[normalizedResource];
    const hasPermission = resourcePermissions[normalizedAction] === true;

    return {
      hasPermission,
      isLoading: false,
      userRole,
    };
  }, [userData?.role, userRoleWithPermissions, resource, action]);
};

/**
 * Hook to check if the current user has any of the specified CRUD permissions
 * 
 * @param {string|string[]} resource - Resource name(s) to check
 * @param {string|string[]} action - Action name(s) to check (e.g., "create", "read", "update", "delete")
 * @param {boolean} requireAll - If true, requires ALL permissions (AND logic). If false, requires ANY permission (OR logic). Default: false
 * @returns {object} - { hasPermission: boolean, isLoading: boolean, userRole: string }
 */
export const useCRUDPermissions = (resource, action, requireAll = false) => {
  const { userData, userRoleWithPermissions } = useSelector((state) => state.auth);

  return useMemo(() => {
    // If resource or action is null/undefined or empty, return early
    if (!resource || !action || (Array.isArray(action) && action.length === 0)) {
      return {
        hasPermission: false,
        isLoading: false,
        userRole: userData?.role || null,
      };
    }

    const userRole = userData?.role || null;

    if (!userRole) {
      return {
        hasPermission: false,
        isLoading: !userData,
        userRole: null,
      };
    }

    // Contractor, Designer, and Supplier roles have full access - bypass permission checks
    // These are the main "superadmin" roles for their respective business types
    const normalizedUserRole = userRole?.toLowerCase();
    if (normalizedUserRole === "contractor" || normalizedUserRole === "designer" || normalizedUserRole === "supplier") {
      return {
        hasPermission: true,
        isLoading: false,
        userRole,
      };
    }

    if (!userRoleWithPermissions) {
      return {
        hasPermission: false,
        isLoading: true,
        userRole,
      };
    }

    const permissions = getPermissionsFromRole(userRoleWithPermissions);
    const actionsToCheck = Array.isArray(action) ? action : [action];
    const normalizedActions = actionsToCheck.map(a => normalizeAction(a));
    const resourcesToCheck = Array.isArray(resource) ? resource : [resource];

    let hasPermission = false;
    if (requireAll) {
      // User must have permission for ALL resources AND ALL actions
      hasPermission = resourcesToCheck.every((res) => {
        const normalizedResource = normalizeResourceType(res);
        const resourcePerms = permissions[normalizedResource];
        return resourcePerms && normalizedActions.every(action => resourcePerms[action] === true);
      });
    } else {
      // User must have permission for ANY resource AND ANY action
      hasPermission = resourcesToCheck.some((res) => {
        const normalizedResource = normalizeResourceType(res);
        const resourcePerms = permissions[normalizedResource];
        return resourcePerms && normalizedActions.some(action => resourcePerms[action] === true);
      });
    }

    return {
      hasPermission,
      isLoading: false,
      userRole,
    };
  }, [userData?.role, userRoleWithPermissions, resource, action, requireAll]);
};

/**
 * Hook to check if the current user has a specific permission
 * Supports both old format (string permissions) and new format (CRUD permissions)
 * 
 * @param {string|object|string[]} permission - Permission to check. Can be:
 *   - String: "CREATE_PROPOSAL" (old format) or "resource:action" (new format)
 *   - Object: { resource: "customer_payments", action: "create" }
 *   - Array: ["CREATE_PROPOSAL", { resource: "customer_payments", action: "read" }]
 * @param {string|string[]} role - Optional: specific role(s) to check (defaults to user's current role)
 * @param {boolean} requireAll - If true, requires ALL permissions (AND logic). If false, requires ANY permission (OR logic). Default: false
 * @returns {object} - { hasPermission: boolean, isLoading: boolean, userRole: string, userPermissions: array }
 */
export const usePermission = (permission, role = null, requireAll = false) => {
  const { userData, userRoleWithPermissions, contractorRoles } = useSelector((state) => state.auth);

  const result = useMemo(() => {
    // If permission is null or undefined, return early
    if (!permission) {
      return {
        hasPermission: false,
        isLoading: false,
        userRole: userData?.role || null,
        userPermissions: [],
      };
    }

    const userRole = userData?.role || null;
    const roleToCheck = role || userRole;

    if (!roleToCheck) {
      return {
        hasPermission: false,
        isLoading: !userData,
        userRole,
        userPermissions: [],
      };
    }

    // Prefer userRoleWithPermissions if available (new CRUD format)
    if (userRoleWithPermissions) {
      const permissions = getPermissionsFromRole(userRoleWithPermissions);
      
      // Normalize permission(s) to check
      const permissionsToCheck = Array.isArray(permission) ? permission : [permission];
      
      let hasPermission = false;
      if (requireAll) {
        hasPermission = permissionsToCheck.every((perm) => {
        if (!perm) return false; // Skip null/undefined permissions
        if (typeof perm === "object" && perm !== null && perm.resource) {
          // CRUD format: { resource: "customer_payments", action: "create" }
          const normalizedAction = normalizeAction(perm.action);
          const normalizedResource = normalizeResourceType(perm.resource);
          const resourcePerms = permissions[normalizedResource];
          return resourcePerms && resourcePerms[normalizedAction] === true;
        } else if (typeof perm === "string" && perm.includes(":")) {
          // Format: "resource:action"
          const [resource, action] = perm.split(":");
          const normalizedAction = normalizeAction(action);
          const normalizedResource = normalizeResourceType(resource);
          const resourcePerms = permissions[normalizedResource];
          return resourcePerms && resourcePerms[normalizedAction] === true;
          } else {
            // Legacy format: check if permission string exists
            return Object.values(permissions).some((resourcePerms) => {
              if (typeof resourcePerms === "object") {
                return Object.values(resourcePerms).some((v) => v === perm);
              }
              return false;
            });
          }
        });
      } else {
        hasPermission = permissionsToCheck.some((perm) => {
          if (!perm) return false; // Skip null/undefined permissions
          if (typeof perm === "object" && perm !== null && perm.resource) {
            const normalizedAction = normalizeAction(perm.action);
            const resourcePerms = permissions[perm.resource];
            return resourcePerms && resourcePerms[normalizedAction] === true;
          } else if (typeof perm === "string" && perm.includes(":")) {
            const [resource, action] = perm.split(":");
            const normalizedAction = normalizeAction(action);
            const resourcePerms = permissions[resource];
            return resourcePerms && resourcePerms[normalizedAction] === true;
          } else {
            return Object.values(permissions).some((resourcePerms) => {
              if (typeof resourcePerms === "object") {
                return Object.values(resourcePerms).some((v) => v === perm);
              }
              return false;
            });
          }
        });
      }

      return {
        hasPermission,
        isLoading: false,
        userRole,
        userPermissions: permissions,
      };
    }

    // Fallback to old format (contractorRoles)
    if (!contractorRoles || contractorRoles.length === 0) {
      return {
        hasPermission: false,
        isLoading: true,
        userRole,
        userPermissions: [],
      };
    }

    // Normalize role to check for case-insensitive matching
    const normalizedRoleToCheck = roleToCheck?.toUpperCase();
    const roleObject = contractorRoles.find((r) => {
      if (typeof r === "string") {
        return r.toUpperCase() === normalizedRoleToCheck;
      } else if (typeof r === "object" && r !== null) {
        return (
          r.name?.toUpperCase() === normalizedRoleToCheck ||
          r.type?.toUpperCase() === normalizedRoleToCheck ||
          r.id === roleToCheck ||
          r.roleType?.toUpperCase() === normalizedRoleToCheck ||
          r.role?.toUpperCase() === normalizedRoleToCheck ||
          r.displayName?.toUpperCase() === normalizedRoleToCheck
        );
      }
      return false;
    });

    if (!roleObject) {
      return {
        hasPermission: false,
        isLoading: false,
        userRole,
        userPermissions: [],
      };
    }

    // Extract permissions from role object (old format)
    let permissions = [];
    if (typeof roleObject === "object" && roleObject !== null) {
      if (Array.isArray(roleObject.permissions)) {
        permissions = roleObject.permissions;
      } else if (Array.isArray(roleObject.permission)) {
        permissions = roleObject.permission;
      } else if (roleObject.permissions && typeof roleObject.permissions === "object") {
        permissions = Object.keys(roleObject.permissions);
      }
    }

    const normalizedPermissions = permissions.map((p) => {
      if (typeof p === "string") {
        return p;
      } else if (typeof p === "object" && p !== null) {
        return p.name || p.id || p.permission || p;
      }
      return String(p);
    });

    const permissionsToCheck = Array.isArray(permission) ? permission : [permission];
    
    let hasPermission = false;
    if (requireAll) {
      hasPermission = permissionsToCheck.every((perm) =>
        normalizedPermissions.some(
          (userPerm) =>
            userPerm === perm ||
            userPerm.toLowerCase() === perm.toLowerCase() ||
            userPerm.includes(perm) ||
            perm.includes(userPerm)
        )
      );
    } else {
      hasPermission = permissionsToCheck.some((perm) =>
        normalizedPermissions.some(
          (userPerm) =>
            userPerm === perm ||
            userPerm.toLowerCase() === perm.toLowerCase() ||
            userPerm.includes(perm) ||
            perm.includes(userPerm)
        )
      );
    }

    return {
      hasPermission,
      isLoading: false,
      userRole,
      userPermissions: normalizedPermissions,
    };
  }, [userData, userRoleWithPermissions, contractorRoles, permission, role, requireAll]);

  return result;
};

/**
 * Hook to check if the current user has a specific role
 * 
 * @param {string|string[]} role - Single role or array of roles to check
 * @returns {boolean} - True if user has one of the specified roles
 */
export const useRole = (role) => {
  const { userData } = useSelector((state) => state.auth);

  return useMemo(() => {
    const userRole = userData?.role || null;
    if (!userRole) return false;

    const rolesToCheck = Array.isArray(role) ? role : [role];
    return rolesToCheck.some(
      (r) =>
        userRole === r ||
        userRole.toLowerCase() === r.toLowerCase()
    );
  }, [userData, role]);
};

/**
 * Hook to get all permissions for the current user's role
 * 
 * @returns {object} - Permissions object (resource -> {create, read, update, delete})
 */
export const useUserPermissions = () => {
  const { userData, userRoleWithPermissions, contractorRoles } = useSelector((state) => state.auth);

  return useMemo(() => {
    if (!userData?.role) {
      return {};
    }

    // Prefer userRoleWithPermissions (new CRUD format)
    if (userRoleWithPermissions) {
      return getPermissionsFromRole(userRoleWithPermissions);
    }

    // Fallback to old format
    if (!contractorRoles || contractorRoles.length === 0) {
      return {};
    }

    const normalizedUserRole = userData.role?.toUpperCase();
    const roleObject = contractorRoles.find((r) => {
      if (typeof r === "string") {
        return r.toUpperCase() === normalizedUserRole;
      } else if (typeof r === "object" && r !== null) {
        return (
          r.name?.toUpperCase() === normalizedUserRole ||
          r.type?.toUpperCase() === normalizedUserRole ||
          r.id === userData.role ||
          r.roleType?.toUpperCase() === normalizedUserRole ||
          r.role?.toUpperCase() === normalizedUserRole ||
          r.displayName?.toUpperCase() === normalizedUserRole
        );
      }
      return false;
    });

    if (!roleObject || typeof roleObject !== "object") {
      return {};
    }

    return getPermissionsFromRole(roleObject);
  }, [userData, userRoleWithPermissions, contractorRoles]);
};
